/**
 * Authentication API Service
 * Handles login, logout, and token management
 */

import API_BASE_URL from './apiConfig';

const AUTH_TOKEN_KEY = 'vacspl_auth_token';
const USER_DATA_KEY = 'vacspl_user_data';

/**
 * Login with employee_id and password
 * @param {string} employee_id - Employee ID (e.g., VACSPL021)
 * @param {string} password - Password
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const login = async (employee_id, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employee_id, password }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.message || `HTTP error! status: ${response.status}`,
      };
    }

    // Store token and user data
    if (result.token && result.user) {
      localStorage.setItem(AUTH_TOKEN_KEY, result.token);
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(result.user));
    }

    return {
      success: true,
      data: result,
    };
  } catch (error) {
    console.error('Error during login:', error);
    return {
      success: false,
      error: error.message || 'Failed to login',
    };
  }
};

/**
 * Logout - clear stored token and user data
 */
export const logout = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
};

/**
 * Get stored authentication token
 * @returns {string|null}
 */
export const getToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Get stored user data
 * @returns {object|null}
 */
export const getUser = () => {
  const userData = localStorage.getItem(USER_DATA_KEY);
  if (!userData) return null;
  try {
    return JSON.parse(userData);
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Check if user is admin
 * @returns {boolean}
 */
export const isAdmin = () => {
  const user = getUser();
  if (!user) return false;
  return user.employee_id === 'VACSPL033' || user.is_admin === true || user.is_super_admin === true;
};

/**
 * Get authorization header for API requests
 * @returns {object} Headers object with Authorization
 */
export const getAuthHeaders = () => {
  const token = getToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
};
